import React, { useState, useEffect, useCallback } from "react";
import api from "../../services/api";
import { MAX_COMPETITORS } from "../../utils/targetInputs";

const MODAL_INPUT = {
  width: "100%",
  boxSizing: "border-box",
  padding: "9px 12px",
  borderRadius: "8px",
  border: "1px solid var(--border)",
  background: "rgba(255,255,255,0.04)",
  color: "var(--text-heading)",
  fontSize: "13px",
  outline: "none",
};

const MODAL_LABEL = {
  fontSize: "11px",
  fontWeight: "600",
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  display: "block",
  marginBottom: "6px",
};

/**
 * Admin-only override for a project's locked domain set.
 *
 * Users cannot change these once saved — this is the escape hatch for typos
 * and for clients who genuinely move domain or swap a competitor. Unlike the
 * user-facing form, competitors here can be cleared and reordered freely.
 */
function EditDomainsModal({ project, onClose, onSaved }) {
  const [domain, setDomain] = useState(project.domain || "");
  const [competitors, setCompetitors] = useState(() => {
    const list = [...(project.competitors || [])];
    while (list.length < MAX_COMPETITORS) list.push("");
    return list.slice(0, MAX_COMPETITORS);
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const res = await api.adminUpdateProjectDomains(project.id, {
        domain: domain.trim(),
        competitors: competitors.map((c) => c.trim()).filter(Boolean),
      });
      onSaved(res.data.project);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !saving) onClose();
      }}
    >
      <form
        onSubmit={handleSubmit}
        style={{
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: "14px",
          padding: "26px",
          width: "100%",
          maxWidth: "440px",
        }}
      >
        <h3
          style={{
            margin: "0 0 4px",
            fontSize: "15px",
            fontWeight: "700",
            color: "var(--text-primary)",
          }}
        >
          Edit domains
        </h3>
        <p
          style={{
            margin: "0 0 20px",
            fontSize: "12px",
            color: "var(--text-muted)",
            lineHeight: "1.6",
          }}
        >
          {project.name} · {project.user?.username || "—"}. Changing these
          changes which sites this project's agent runs can target. Past
          executions keep the domains they ran with.
        </p>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: "16px" }}>
            <span>⚠</span> {error}
          </div>
        )}

        <div style={{ marginBottom: "18px" }}>
          <label style={MODAL_LABEL}>Main Domain</label>
          <input
            style={MODAL_INPUT}
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="acme.com"
            disabled={saving}
            autoComplete="off"
            spellCheck={false}
            required
          />
        </div>

        <div style={{ marginBottom: "22px" }}>
          <label style={MODAL_LABEL}>Competitors (up to {MAX_COMPETITORS})</label>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {competitors.map((value, i) => (
              <input
                key={i}
                style={MODAL_INPUT}
                value={value}
                onChange={(e) =>
                  setCompetitors((prev) => {
                    const next = [...prev];
                    next[i] = e.target.value;
                    return next;
                  })
                }
                placeholder={`Competitor ${i + 1} — leave blank to remove`}
                disabled={saving}
                autoComplete="off"
                spellCheck={false}
              />
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            style={{
              padding: "9px 18px",
              background: "transparent",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              color: "var(--text-muted)",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button type="submit" className="btn" disabled={saving}>
            {saving ? "Saving…" : "Save domains"}
          </button>
        </div>
      </form>
    </div>
  );
}

function timeAgo(d) {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Delete confirmation for an admin-initiated project delete.
 *
 * Deliberately spells out the three consequences, because none of them are
 * obvious from the button: execution history disappears from the owner's view,
 * the owner gets their quota slot back, and the domain lock is released so a
 * replacement project can use a different domain.
 */
function DeleteProjectModal({ project, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await api.adminDeleteProject(project.id);
      onDeleted(project.id);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setDeleting(false);
    }
  };

  const execCount = project.executionCount || 0;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.7)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !deleting) onClose();
      }}
    >
      <div
        style={{
          background: "var(--surface)",
          border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: "14px",
          padding: "26px",
          width: "100%",
          maxWidth: "460px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              borderRadius: "10px",
              background: "rgba(239,68,68,0.1)",
              border: "1px solid rgba(239,68,68,0.25)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path
                d="M3 5h12M7 5V3h4v2M6 5v9a1 1 0 001 1h4a1 1 0 001-1V5H6z"
                stroke="#f87171"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <div
              style={{
                fontSize: "15px",
                fontWeight: "700",
                color: "var(--text-primary)",
              }}
            >
              Delete this project?
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "var(--text-muted)",
                marginTop: "2px",
              }}
            >
              {project.name}
              {project.domain ? ` · ${project.domain}` : ""} ·{" "}
              {project.user?.username || "unknown owner"}
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: "16px" }}>
            <span>⚠</span> {error}
          </div>
        )}

        <div
          style={{
            padding: "14px",
            borderRadius: "10px",
            background: "rgba(239,68,68,0.06)",
            border: "1px solid rgba(239,68,68,0.15)",
            marginBottom: "20px",
          }}
        >
          <ul
            style={{
              margin: 0,
              paddingLeft: "18px",
              fontSize: "13px",
              color: "var(--text-secondary)",
              lineHeight: "1.7",
            }}
          >
            <li>
              {execCount > 0 ? (
                <>
                  <strong style={{ color: "#f87171" }}>
                    {execCount} execution{execCount === 1 ? "" : "s"}
                  </strong>{" "}
                  will be removed from the owner's history.
                </>
              ) : (
                "This project has no executions."
              )}
            </li>
            <li>
              {project.user?.username || "The owner"} gets a project slot back
              and can create a replacement.
            </li>
            <li>
              The domain lock on{" "}
              <strong style={{ color: "var(--text-primary)" }}>
                {project.domain || "this project"}
              </strong>{" "}
              is released — the replacement may use a different domain.
            </li>
          </ul>
        </div>

        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            style={{
              padding: "9px 18px",
              background: "transparent",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              color: "var(--text-muted)",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={deleting}
            style={{
              padding: "9px 18px",
              background: "rgba(239,68,68,0.15)",
              border: "1px solid rgba(239,68,68,0.35)",
              borderRadius: "8px",
              color: "#f87171",
              fontSize: "13px",
              fontWeight: "600",
              cursor: deleting ? "not-allowed" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            {deleting && (
              <span
                className="spinner"
                style={{
                  width: "12px",
                  height: "12px",
                  borderWidth: "2px",
                  borderColor: "#f87171",
                  borderTopColor: "transparent",
                }}
              />
            )}
            {deleting ? "Deleting…" : "Delete project"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ProjectsTab() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [showDeleted, setShowDeleted] = useState(true);
  const [editingProject, setEditingProject] = useState(null);
  const [deletingProject, setDeletingProject] = useState(null);

  const load = useCallback(async (includeDeleted) => {
    setLoading(true);
    try {
      const res = await api.adminGetProjects(
        includeDeleted ? { includeDeleted: true } : undefined,
      );
      setProjects(res.data.projects || []);
    } catch (err) {
      console.error("Failed to load projects:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(showDeleted);
  }, [load, showDeleted]);

  const filtered = search.trim()
    ? projects.filter(
        (p) =>
          p.name.toLowerCase().includes(search.toLowerCase()) ||
          p.user?.username?.toLowerCase().includes(search.toLowerCase()) ||
          p.user?.email?.toLowerCase().includes(search.toLowerCase()) ||
          p.domain?.toLowerCase().includes(search.toLowerCase()) ||
          (p.competitors || []).some((c) =>
            c.toLowerCase().includes(search.toLowerCase()),
          ) ||
          p.context?.toLowerCase().includes(search.toLowerCase()),
      )
    : projects;

  const handleDomainsSaved = (updated) => {
    setProjects((prev) =>
      prev.map((p) =>
        p.id === updated.id
          ? { ...p, domain: updated.domain, competitors: updated.competitors }
          : p,
      ),
    );
    setEditingProject(null);
  };

  const handleDeleted = (id) => {
    // Soft-delete: keep the row when deleted projects are on screen, otherwise
    // drop it, so the list matches the current filter either way.
    setProjects((prev) =>
      showDeleted
        ? prev.map((p) =>
            p.id === id ? { ...p, deletedAt: new Date().toISOString() } : p,
          )
        : prev.filter((p) => p.id !== id),
    );
    setDeletingProject(null);
  };

  const CELL = {
    fontSize: "13px",
    color: "var(--text-secondary)",
    verticalAlign: "top",
    padding: "12px 14px",
  };
  const HEAD = {
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "var(--text-muted)",
    padding: "10px 14px",
    borderBottom: "1px solid var(--border)",
    background: "rgba(255,255,255,0.02)",
  };

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2
            style={{
              fontSize: "16px",
              fontWeight: "700",
              color: "var(--text-heading)",
              margin: 0,
            }}
          >
            Projects Master
          </h2>
          <p
            style={{
              fontSize: "12px",
              color: "var(--text-muted)",
              margin: "4px 0 0",
            }}
          >
            All projects created by users across the platform
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap",
          }}
        >
          {/* Deleted toggle */}
          <button
            onClick={() => setShowDeleted((v) => !v)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "12px",
              padding: "5px 12px",
              borderRadius: "7px",
              border: `1px solid ${showDeleted ? "var(--border)" : "rgba(239,68,68,0.4)"}`,
              background: showDeleted
                ? "rgba(255,255,255,0.04)"
                : "rgba(239,68,68,0.08)",
              color: showDeleted ? "var(--text-muted)" : "#f87171",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            <span style={{ fontSize: "11px" }}>🗑</span>
            {showDeleted ? "Hide deleted" : "Show deleted"}
          </button>

          <span
            style={{
              fontSize: "12px",
              color: "var(--text-muted)",
              padding: "4px 10px",
              borderRadius: "6px",
              background: "rgba(255,255,255,0.04)",
              border: "1px solid var(--border)",
            }}
          >
            {filtered.length} project{filtered.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={() => load(showDeleted)}
            disabled={loading}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "5px",
              fontSize: "12px",
              padding: "5px 12px",
              borderRadius: "7px",
              border: "1px solid var(--border)",
              background: "rgba(255,255,255,0.04)",
              color: "var(--text-muted)",
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? (
              <span
                className="spinner"
                style={{ width: "11px", height: "11px", borderWidth: "2px" }}
              />
            ) : (
              "↻"
            )}
            Refresh
          </button>
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: "16px" }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by project name, user, or context…"
          style={{
            width: "100%",
            boxSizing: "border-box",
            padding: "9px 14px",
            borderRadius: "9px",
            border: "1px solid var(--border)",
            background: "rgba(255,255,255,0.04)",
            color: "var(--text-heading)",
            fontSize: "13px",
            outline: "none",
          }}
        />
      </div>

      {/* Table */}
      <div
        style={{
          background: "var(--bg-card)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {loading ? (
          <div style={{ padding: "48px", textAlign: "center" }}>
            <div className="spinner-lg" />
            <div
              style={{
                fontSize: "13px",
                color: "var(--text-muted)",
                marginTop: "12px",
              }}
            >
              Loading projects…
            </div>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: "48px", textAlign: "center" }}>
            <div
              style={{ fontSize: "32px", marginBottom: "10px", opacity: 0.4 }}
            >
              📁
            </div>
            <div
              style={{
                fontSize: "14px",
                color: "var(--text-secondary)",
                marginBottom: "6px",
              }}
            >
              {search ? "No projects match your search" : "No projects yet"}
            </div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
              Projects are created by users from the Available Agents page
            </div>
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={HEAD}>Project</th>
                  <th style={{ ...HEAD, minWidth: "180px" }}>Domains</th>
                  <th style={{ ...HEAD, minWidth: "130px" }}>Owner</th>
                  <th style={{ ...HEAD, minWidth: "100px" }}>Created</th>
                  {showDeleted && (
                    <th style={{ ...HEAD, minWidth: "110px" }}>Deleted</th>
                  )}
                  <th style={{ ...HEAD, width: "60px" }}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((project) => {
                  const isDeleted = !!project.deletedAt;
                  return (
                    <React.Fragment key={project.id}>
                      <tr
                        style={{
                          borderBottom: "1px solid var(--border)",
                          transition: "background 0.1s",
                          opacity: isDeleted ? 0.6 : 1,
                          background: isDeleted
                            ? "rgba(239,68,68,0.03)"
                            : "transparent",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.background = isDeleted
                            ? "rgba(239,68,68,0.06)"
                            : "rgba(255,255,255,0.02)")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.background = isDeleted
                            ? "rgba(239,68,68,0.03)"
                            : "transparent")
                        }
                      >
                        {/* Project name + context */}
                        <td style={CELL}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "8px",
                            }}
                          >
                            <div
                              style={{
                                width: "28px",
                                height: "28px",
                                borderRadius: "6px",
                                background: isDeleted
                                  ? "rgba(239,68,68,0.1)"
                                  : "rgba(6,182,212,0.1)",
                                border: `1px solid ${isDeleted ? "rgba(239,68,68,0.2)" : "rgba(6,182,212,0.2)"}`,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                marginTop: "1px",
                              }}
                            >
                              <svg
                                width="13"
                                height="13"
                                viewBox="0 0 16 16"
                                fill="none"
                              >
                                <path
                                  d="M1.5 3.5A1 1 0 012.5 2.5h4l1.5 2H13.5a1 1 0 011 1v7a1 1 0 01-1 1h-11a1 1 0 01-1-1v-9z"
                                  stroke={
                                    isDeleted ? "#f87171" : "var(--teal-bright)"
                                  }
                                  strokeWidth="1.3"
                                  fill="none"
                                />
                              </svg>
                            </div>
                            <div>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "7px",
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    fontWeight: "600",
                                    color: "var(--text-heading)",
                                    fontSize: "13px",
                                    textDecoration: isDeleted
                                      ? "line-through"
                                      : "none",
                                    opacity: isDeleted ? 0.7 : 1,
                                  }}
                                >
                                  {project.name}
                                </span>
                                {isDeleted && (
                                  <span
                                    style={{
                                      fontSize: "10px",
                                      fontWeight: "700",
                                      padding: "2px 6px",
                                      borderRadius: "4px",
                                      background: "rgba(239,68,68,0.12)",
                                      border: "1px solid rgba(239,68,68,0.25)",
                                      color: "#f87171",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.05em",
                                    }}
                                  >
                                    Deleted
                                  </span>
                                )}
                              </div>
                              {project.context && (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "var(--text-muted)",
                                    marginTop: "2px",
                                    maxWidth: "320px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace:
                                      expandedId === project.id
                                        ? "normal"
                                        : "nowrap",
                                    lineHeight: "1.5",
                                  }}
                                >
                                  {project.context}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Locked domain set — admin-editable */}
                        <td style={CELL}>
                          {project.domain ? (
                            <>
                              <div
                                style={{
                                  fontWeight: "600",
                                  color: "var(--text-heading)",
                                }}
                              >
                                {project.domain}
                              </div>
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "var(--text-muted)",
                                  marginTop: "2px",
                                  lineHeight: "1.5",
                                }}
                              >
                                {project.competitors?.length
                                  ? `vs ${project.competitors.join(", ")}`
                                  : "no competitors"}
                              </div>
                            </>
                          ) : (
                            <span
                              style={{
                                fontSize: "11px",
                                fontWeight: "700",
                                padding: "2px 7px",
                                borderRadius: "5px",
                                background: "rgba(245,158,11,0.1)",
                                border: "1px solid rgba(245,158,11,0.3)",
                                color: "#f59e0b",
                              }}
                            >
                              Not set
                            </span>
                          )}
                          {!isDeleted && (
                            <div
                              style={{
                                marginTop: "6px",
                                display: "flex",
                                gap: "6px",
                              }}
                            >
                              <button
                                onClick={() => setEditingProject(project)}
                                style={{
                                  background: "rgba(255,255,255,0.05)",
                                  border: "1px solid var(--border)",
                                  borderRadius: "5px",
                                  color: "var(--text-muted)",
                                  cursor: "pointer",
                                  padding: "3px 9px",
                                  fontSize: "11px",
                                }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => setDeletingProject(project)}
                                style={{
                                  background: "rgba(239,68,68,0.08)",
                                  border: "1px solid rgba(239,68,68,0.2)",
                                  borderRadius: "5px",
                                  color: "#f87171",
                                  cursor: "pointer",
                                  padding: "3px 9px",
                                  fontSize: "11px",
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </td>

                        {/* Owner */}
                        <td style={CELL}>
                          <div
                            style={{
                              fontWeight: "500",
                              color: "var(--text-secondary)",
                            }}
                          >
                            {project.user?.username || "—"}
                          </div>
                          {project.user?.email && (
                            <div
                              style={{
                                fontSize: "11px",
                                color: "var(--text-muted)",
                                marginTop: "2px",
                              }}
                            >
                              {project.user.email}
                            </div>
                          )}
                        </td>

                        {/* Created */}
                        <td style={CELL}>
                          <div style={{ color: "var(--text-secondary)" }}>
                            {formatDate(project.createdAt)}
                          </div>
                          <div
                            style={{
                              fontSize: "11px",
                              color: "var(--text-muted)",
                              marginTop: "2px",
                            }}
                          >
                            {timeAgo(project.createdAt)}
                          </div>
                        </td>

                        {/* Deleted date (only when showDeleted is on) */}
                        {showDeleted && (
                          <td style={CELL}>
                            {isDeleted ? (
                              <>
                                <div style={{ color: "#f87171" }}>
                                  {formatDate(project.deletedAt)}
                                </div>
                                <div
                                  style={{
                                    fontSize: "11px",
                                    color: "var(--text-muted)",
                                    marginTop: "2px",
                                  }}
                                >
                                  {timeAgo(project.deletedAt)}
                                </div>
                              </>
                            ) : (
                              <span
                                style={{
                                  color: "var(--text-muted)",
                                  fontSize: "12px",
                                }}
                              >
                                —
                              </span>
                            )}
                          </td>
                        )}

                        {/* Expand toggle (for project context) */}
                        <td style={{ ...CELL, textAlign: "center" }}>
                          {project.context ? (
                            <button
                              onClick={() =>
                                setExpandedId(
                                  expandedId === project.id ? null : project.id,
                                )
                              }
                              style={{
                                background: "rgba(255,255,255,0.05)",
                                border: "1px solid var(--border)",
                                borderRadius: "5px",
                                color: "var(--text-muted)",
                                cursor: "pointer",
                                padding: "3px 8px",
                                fontSize: "11px",
                              }}
                            >
                              {expandedId === project.id ? "▲" : "▼"}
                            </button>
                          ) : null}
                        </td>
                      </tr>

                      {/* Expanded project context */}
                      {expandedId === project.id && project.context && (
                        <tr style={{ borderBottom: "1px solid var(--border)" }}>
                          <td
                            colSpan={showDeleted ? 6 : 5}
                            style={{ padding: "0" }}
                          >
                            <div
                              style={{
                                padding: "14px 20px 14px 56px",
                                background: "rgba(6,182,212,0.025)",
                                borderTop: "1px solid rgba(6,182,212,0.1)",
                              }}
                            >
                              <div
                                style={{
                                  fontSize: "11px",
                                  fontWeight: "700",
                                  letterSpacing: "0.06em",
                                  textTransform: "uppercase",
                                  color: "var(--text-muted)",
                                  marginBottom: "6px",
                                }}
                              >
                                Project Context
                              </div>
                              <p
                                style={{
                                  fontSize: "13px",
                                  color: "var(--text-secondary)",
                                  lineHeight: "1.6",
                                  margin: 0,
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {project.context}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editingProject && (
        <EditDomainsModal
          project={editingProject}
          onClose={() => setEditingProject(null)}
          onSaved={handleDomainsSaved}
        />
      )}

      {deletingProject && (
        <DeleteProjectModal
          project={deletingProject}
          onClose={() => setDeletingProject(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
